import { Dispatch, SetStateAction } from "react";
import { useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";

import Radio from "components/atoms/Radio/Radio";
import usePermissions from "hooks/usePermissions";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import { RequestFormState } from "pages/shipper/RequestForm/RequestFormState";
import testIds from "constants/testIds";
import { pickupDeliveryOption } from "../ScheduleDefaultOptions";

interface AddressTypeFormProps {
  isPickupOrDelivery: string;
  setPortAddressOption: Dispatch<SetStateAction<any>>;
  formState: RequestFormState;
}

export default function AddressTypeForm({ isPickupOrDelivery, setPortAddressOption, formState }: AddressTypeFormProps) {
  const intl = useIntl();
  const { permissions }: any = usePermissions();
  const { enable_new_address_structure } = JSON.parse(permissions)?.company_shipper_settings;

  const { getValues, setValue, clearErrors } = useFormContext();

  const handleChangeAddressType = (event: any) => {
    const nextAddressType = event?.target?.value ?? event?.value;

    // Radio.customOnChange fires before RHF setValue in Radio component.
    // Use the incoming event value instead of getValues() to avoid stale reads.
    if (!nextAddressType) return;

    setPortAddressOption([]);
    setValue(`schedule.${isPickupOrDelivery}_airport`, "");
    setValue(`schedule.${isPickupOrDelivery}_airport_code`, "");
    setValue(`schedule.${isPickupOrDelivery}_seaport`, "");
    setValue(`schedule.${isPickupOrDelivery}_seaport_code`, "");
    setValue(`schedule.${isPickupOrDelivery}_port_city`, "");
    setValue(`schedule.${isPickupOrDelivery}_port_country`, "");
    setValue(`schedule.${isPickupOrDelivery}_port_country_code`, "");

    // Always clear address selection when switching among address/airport/seaport.
    // customOnChange runs before address_type is updated on the form, so do not
    // validate here — required rules for address_name still reflect the previous type.
    const addressNameField = `schedule.${isPickupOrDelivery}_address_name`;
    const timezoneField = `schedule.${isPickupOrDelivery}_timezone`;
    clearErrors([addressNameField, timezoneField]);
    setValue(addressNameField, "", { shouldValidate: false });
  };

  return (
    <div>
      {enable_new_address_structure ? (
        <div
          data-testid={
            isPickupOrDelivery === "pickup"
              ? testIds.REQUEST_FORM_SCHEDULE_PICKUP_ADDRESS_TYPE
              : testIds.REQUEST_FORM_SCHEDULE_DELIVERY_ADDRESS_TYPE
          }
        >
          <Radio
            className="border-0 d-flex flex-row width-fit p-none mb-xl"
            customOption
            name={`schedule.${isPickupOrDelivery}_address_type`}
            defaultChecked={getValues(`schedule.${isPickupOrDelivery}_address_type`)}
            options={pickupDeliveryOption(intl)}
            customColClass="pl-0"
            customOnChange={handleChangeAddressType}
            // TODO: Implemented disabled state  for Radio component
            // disabled={formState.disabledFields[`schedule.${isPickupOrDelivery}_address_type`]}
          />
        </div>
      ) : (
        <div
          data-testid={
            isPickupOrDelivery === "pickup"
              ? testIds.REQUEST_FORM_SCHEDULE_PICKUP_ADDRESS_TYPE
              : testIds.REQUEST_FORM_SCHEDULE_DELIVERY_ADDRESS_TYPE
          }
        >
          <div className="w-192 mr-md mb-xl">
            <Dropdown
              name={`schedule.${isPickupOrDelivery}_address_type`}
              className="schedule-dropdown"
              dataTestid={`schedule.${isPickupOrDelivery}_address_type`}
              options={pickupDeliveryOption(intl)}
              defaultValue={pickupDeliveryOption(intl)?.[0]?.value}
              useHookForm
              onChange={handleChangeAddressType}
              disabled={formState.disabledFields[`schedule.${isPickupOrDelivery}_address_type`]}
            />
          </div>
        </div>
      )}
    </div>
  );
}
